import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ColorModeIconDropdown from '../../shared-theme/ColorModeIconDropdown';
import Sitemark from '../../components/SitemarkIcon'
import { Link } from 'react-router-dom';

const StyledToolbar = styled(Toolbar, {
  shouldForwardProp: (prop) => prop !== 'heroMode',
})(({ theme, heroMode }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: heroMode ? 'rgba(85,180,172,0.2)' : (theme.vars || theme).palette.divider,
  backgroundColor: heroMode
    ? 'rgba(8,23,42,0.5)'
    : theme.vars
      ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
      : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
  transition: 'background-color 0.35s ease, border-color 0.35s ease',
}));

export default function AppAppBar() {
  const [open, setOpen] = React.useState(false);
  const [onHero, setOnHero] = React.useState(true);

  React.useEffect(() => {
    const check = () => {
      const hero = document.getElementById('hero');
      setOnHero(hero ? hero.getBoundingClientRect().bottom > 80 : false);
    };
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, []);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Offset for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setOpen(false); // Close drawer on mobile after clicking
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters heroMode={onHero}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <Box
              onClick={scrollToTop}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                filter: onHero ? 'brightness(0) invert(1)' : 'none',
                transition: 'filter 0.35s ease',
              }}
            >
              <Sitemark />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              {['features', 'pricing', 'faq'].map((id, i) => (
                <Button
                  key={id}
                  variant="text"
                  size="small"
                  onClick={() => scrollToSection(id)}
                  sx={{
                    color: onHero ? 'rgba(255,255,255,0.85)' : undefined,
                    minWidth: i === 2 ? 0 : undefined,
                    transition: 'color 0.35s ease',
                    '&:hover': { color: onHero ? '#ffffff' : undefined },
                  }}
                >
                  {['Características', 'Precios', 'Preguntas frecuentes'][i]}
                </Button>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
            <Button
              variant="text"
              size="small"
              component={Link}
              to="/login"
              sx={{
                fontWeight: 600,
                color: onHero ? 'rgba(255,255,255,0.9)' : undefined,
                transition: 'color 0.35s ease',
                '&:hover': { color: onHero ? '#ffffff' : undefined },
              }}
            >
              Iniciar sesión
            </Button>
            {/*<Button color="primarybutton" variant="contained" size="small" component={Link} to="/onBoarding" sx={{fontWeight: 600}}>
              Registrarse
            </Button>*/}
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ColorModeIconDropdown size="medium" />
            <IconButton
              aria-label="Menu button"
              onClick={toggleDrawer(true)}
              sx={{ color: onHero ? 'rgba(255,255,255,0.85)' : undefined, transition: 'color 0.35s ease' }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                <MenuItem onClick={() => scrollToSection('features')}>Características</MenuItem>
                <MenuItem onClick={() => scrollToSection('testimonials')}>Testimonios</MenuItem>
                <MenuItem onClick={() => scrollToSection('highlights')}>Destacados</MenuItem>
                <MenuItem onClick={() => scrollToSection('pricing')}>Precios</MenuItem>
                <MenuItem onClick={() => scrollToSection('faq')}>Preguntas frecuentes</MenuItem>
                <Divider sx={{ my: 3 }} />
              {/*  <MenuItem>
                  <Button 
                    color="primary" 
                    variant="contained" 
                    fullWidth
                    component={Link}
                    to="/onBoarding"
                  >
                    Registrarse
                  </Button>
                </MenuItem>*/}
                <MenuItem>
                  <Button 
                    color="primary" 
                    variant="outlined" 
                    fullWidth
                    component={Link}
                    to="/login"
                  >
                    Iniciar sesión
                  </Button>
                </MenuItem>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
